/**
 * Helper functions for processing passage text and HTML
 * Used by ListeningTestUI and ListeningTestUI2
 */

/**
 * Find question number from previous siblings (including inside elements)
 */
export function findQuestionNumberFromSiblings(node) {
  while (node) {
    if (node.nodeType === 3) { // text node
      const match = node.textContent.match(/<(\d+)>/);
      if (match) return match[1];
    } else if (node.nodeType === 1) { // element node
      // Check its text content for the pattern
      const match = node.textContent.match(/<(\d+)>/);
      if (match) return match[1];
    }
    node = node.previousSibling;
  }
  return null;
}

/**
 * Clean angle bracket markers from HTML (used for question-box content)
 */
function cleanAngleBrackets(html) {
  if (!html) return html;
  return html
    .replace(/(&lt;(\d+)&gt;)/g, '') // HTML entities like &lt;1&gt;
    .replace(/(<(\d+)>)/g, '');      // Actual brackets like <1>
}

/**
 * Extract blank-to-number mapping and clean HTML
 */
export function extractBlankNumberMappingAndCleanHtml(html) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const blankToNumber = {};
  const selectionToNumber = {};

  // STEP 1: Map each blank to its question number BEFORE cleaning markers
  // Process each paragraph to find blanks and their preceding markers
  doc.querySelectorAll('p').forEach(p => {
    const pHTML = p.innerHTML;
    // Find all markers and blanks in order
    const markers = [];
    const blanks = [];
    
    // Find all angle bracket markers with their positions
    const markerRegex = /(&lt;(\d+)&gt;|<(\d+)>)/g;
    let markerMatch;
    while ((markerMatch = markerRegex.exec(pHTML)) !== null) {
      markers.push({
        number: markerMatch[2] || markerMatch[3],
        index: markerMatch.index
      });
    }
    
    // Find all blank spans (include table blanks inside question-box-content so they get marker-based numbers like <1>, <2>)
    p.querySelectorAll('.tiptap-input-field').forEach(span => {
      const inTable = span.closest('table');
      const inQuestionBoxContent = span.closest('.question-box-content');
      if (!inTable || inQuestionBoxContent) {
        const spanHTML = span.outerHTML;
        const spanIndex = pHTML.indexOf(spanHTML);
        if (spanIndex !== -1) {
          blanks.push({
            span: span,
            index: spanIndex
          });
        }
      }
    });

    // Find all selection spans (include table blanks inside question-box-content for marker-based numbers)
    const selections = [];
    p.querySelectorAll('.selection-field').forEach(span => {
      const inTable = span.closest('table');
      const inQuestionBoxContent = span.closest('.question-box-content');
      if (!inTable || inQuestionBoxContent) {
        const spanHTML = span.outerHTML;
        const spanIndex = pHTML.indexOf(spanHTML);
        if (spanIndex !== -1) {
          selections.push({
            span: span,
            index: spanIndex
          });
        }
      }
    });
    
    // Match each blank to its nearest preceding marker
    blanks.forEach(blank => {
      // Find the marker that comes just before this blank
      let matchedMarker = null;
      for (let i = markers.length - 1; i >= 0; i--) {
        if (markers[i].index < blank.index) {
          matchedMarker = markers[i];
          break;
        }
      }
      
      if (matchedMarker && blank.span && blank.span.id) {
        blankToNumber[blank.span.id] = matchedMarker.number;
      }
    });

    selections.forEach(selection => {
      let matchedMarker = null;
      for (let i = markers.length - 1; i >= 0; i--) {
        if (markers[i].index < selection.index) {
          matchedMarker = markers[i];
          break;
        }
      }
      if (matchedMarker && selection.span && selection.span.id) {
        selectionToNumber[selection.span.id] = matchedMarker.number;
      }
    });
  });

  // STEP 2: NOW clean angle brackets for display
  doc.querySelectorAll('.question-box-content').forEach(boxContent => {
    if (boxContent.innerHTML) {
      boxContent.innerHTML = cleanAngleBrackets(boxContent.innerHTML);
    }
  });

  // Remove ALL angle-bracket markers from paragraphs for display
  doc.querySelectorAll('p').forEach(p => {
    p.innerHTML = p.innerHTML.replace(/(&lt;\d+&gt;|<\d+>)/g, '');
  });

  return {
    cleanedHtml: doc.body.innerHTML,
    blankToNumber,
    selectionToNumber
  };
}

/**
 * Process passage text to replace blanks with placeholders for hydration
 */
export function processPassageText(html) {
  return html.replace(
    /<span class=\"tiptap-input-field\" id=\"(blank-[^\"]+)\">.*?<\/span>/g,
    (match, id) => `<span data-blank-id="${id}"></span>`
  );
}

